import styles from './captureFlash.module.scss';

const FLASH_MS = 90;
const FLY_MS = 520;
const HOLD_MS = 650;

/** The visible Bildir nav item — the tab bar on mobile, the sidebar on desktop. */
function reportNavRect(): DOMRect | null {
  const targets = [...document.querySelectorAll<HTMLElement>('[data-tab="report"]')];
  const visible = targets.find((el) => el.getBoundingClientRect().width > 0);
  return visible?.getBoundingClientRect() ?? null;
}

/**
 * Plays the capture back at the user: the image itself flying into the Bildir
 * button, so they watch the thing they are about to send land where it lands.
 *
 * Reduced motion gets a still frame rather than nothing. Skipping it entirely
 * left those users — a real share of them, since Windows turns this on with its
 * own animation setting — with a screenshot attached out of nowhere.
 */
export function playCaptureFlash(file: File): Promise<void> {
  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

  const url = URL.createObjectURL(file);
  const layer = document.createElement('div');
  layer.className = styles.layer;
  layer.setAttribute('data-capture-ignore', '');
  layer.setAttribute('aria-hidden', 'true');

  const flash = document.createElement('div');
  flash.className = styles.flash;

  const shot = document.createElement('img');
  shot.className = styles.shot;
  shot.src = url;
  shot.alt = '';

  layer.append(flash, shot);
  document.body.appendChild(layer);

  const done = () => {
    layer.remove();
    URL.revokeObjectURL(url);
  };

  // A decoded image is the whole point of the effect; without it the user sees
  // an empty rectangle move.
  return shot
    .decode()
    .catch(() => {})
    .then(() => {
      if (reduced) {
        // Held still, then faded: no travel, no luminance flash, nothing that
        // moves — but the capture is unmistakably shown before it is sent.
        shot.classList.add(styles.still);
        const hold = shot.animate([{ opacity: 1 }, { opacity: 1 }, { opacity: 0 }], {
          duration: HOLD_MS,
          easing: 'linear',
          fill: 'forwards',
        });
        return hold.finished.catch(() => {}).then(done);
      }

      flash.animate([{ opacity: 0 }, { opacity: 0.9 }, { opacity: 0 }], {
        duration: FLASH_MS,
        easing: 'ease-out',
      });

      const target = reportNavRect();
      const endScale = target ? Math.max(target.width / window.innerWidth, 0.08) : 0.18;
      const endX = target ? target.left + target.width / 2 - window.innerWidth / 2 : 0;
      const endY = target
        ? target.top + target.height / 2 - window.innerHeight / 2
        : window.innerHeight * 0.4;

      const fly = shot.animate(
        [
          { transform: 'translate(0,0) scale(1)', opacity: 1, borderRadius: '0px' },
          {
            transform: `translate(${endX}px, ${endY}px) scale(${endScale})`,
            opacity: 0.15,
            borderRadius: '24px',
          },
        ],
        { duration: FLY_MS, easing: 'cubic-bezier(0.4, 0, 0.2, 1)', fill: 'forwards' }
      );

      return fly.finished.catch(() => {}).then(done);
    });
}
